import { MAX_USERNAME_LENGTH } from "@lorevok/domain";
import { useT } from "../lib/i18n/store.js";
import { usernameStatusMessageKey, type UsernameStatus } from "../lib/useUsernameAvailability.js";

/**
 * The sign-up form's username input: the rules (and that the name is
 * permanent) under it, plus a live status line once something is typed —
 * invalid / reserved / checking / available / taken.
 */
export function UsernameField({
  id,
  value,
  onChange,
  status
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  status: UsernameStatus;
}) {
  const t = useT();
  const problemKey = status === "empty" ? null : usernameStatusMessageKey(status);
  const message =
    problemKey !== null
      ? t(problemKey)
      : status === "checking"
        ? t("username.checking")
        : status === "available"
          ? t("username.available")
          : null;
  const tone = problemKey !== null ? "bad" : status === "available" ? "good" : "neutral";
  const statusId = `${id}-status`;
  const hintId = `${id}-hint`;
  return (
    <div className="auth-field">
      <label htmlFor={id}>{t("username.label")}</label>
      <input
        id={id}
        type="text"
        name="username"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        maxLength={MAX_USERNAME_LENGTH}
        placeholder={t("username.placeholder")}
        value={value}
        aria-invalid={problemKey !== null || undefined}
        aria-describedby={`${statusId} ${hintId}`}
        onChange={(event) => onChange(event.target.value)}
      />
      {message && (
        <span id={statusId} className={`auth-hint username-status username-status--${tone}`} aria-live="polite">
          {message}
        </span>
      )}
      <span id={hintId} className="auth-hint">
        {t("username.hint")}
      </span>
    </div>
  );
}
